/**
 * @version     $Id$
 * @package     Shopify_Pricing_Calculator
 * @author      PageFly Team <admin@pagefly.io>
 * @copyright   Copyright (C) 2019 PageFly.io. All Rights Reserved.
 * @license     GNU/GPL v2 or later http://www.gnu.org/licenses/gpl-2.0.html
 */

// Import necessary libraries.
import './App.css'
import React from 'react'

export default class extends React.Component {
	static defaultProps = {
		shopifyPlans: {
			'Basic Shopify': {
				creditCardRatePercentage: 2.9,
				creditCardRateFixed: 0.3,
				transactionFee: 2,
				monthlyPrice: 39
			},
			'Shopify': {
				creditCardRatePercentage: 2.6,
				creditCardRateFixed: 0.3,
				transactionFee: 1,
				monthlyPrice: 105
			},
			'Advanced Shopify': {
				creditCardRatePercentage: 2.4,
				creditCardRateFixed: 0.3,
				transactionFee: 0.5,
				monthlyPrice: 399
			}
		}
	}

	state = {
		averageOrderValue: 0,
		numberOfOrderPerMonth: 0,
		useShopifyPayment: 'yes',
		externalPaymentGatewayFeesPercentage: 0,
		externalPaymentGatewayFeesFixed: 0
	}

	calculateFee(avgPrice, numOrder, feePercentage, feeFixed = 0) {
		return parseFloat(
			(((avgPrice * numOrder) / 100) * feePercentage + numOrder * feeFixed).toFixed(2)
		)
	}

	render() {
		const {shopifyPlans} = this.props
		const {
			averageOrderValue,
			numberOfOrderPerMonth,
			useShopifyPayment,
			externalPaymentGatewayFeesPercentage,
			externalPaymentGatewayFeesFixed
		} = this.state

		return <div className="w">
			<h2>Shopify Pricing Calculator</h2>
			<div className="g">
				<div className="i">
					<input
						type="number"
						defaultValue={averageOrderValue || ''}
						className={averageOrderValue ? 'hasValue' : ''}
						onChange={e => this.setState({averageOrderValue: e.target.value})}
					/>
					<span className="ph" data-title="What is your average order value?"></span>
				</div>
				<div className="i">
					<input
						type="number"
						defaultValue={numberOfOrderPerMonth || ''}
						className={numberOfOrderPerMonth ? 'hasValue' : ''}
						onChange={e => this.setState({numberOfOrderPerMonth: e.target.value})}
					/>
					<span className="ph" data-title="What is your number of orders per month?"></span>
				</div>
			</div>
			<div className="g" style={{margin: '30px 0'}}>
				<div>Do you use Shopify payments?</div>
				<div>
					<input
						type="radio"
						name="useShopifyPayment"
						defaultValue="yes"
						defaultChecked={useShopifyPayment === 'yes'}
						onClick={e => this.setState({useShopifyPayment: e.target.value})}
					/>
					<label style={{marginRight: '10px'}}>Yes</label>
					<input
						type="radio"
						name="useShopifyPayment"
						defaultValue="no"
						defaultChecked={useShopifyPayment === 'no'}
						onClick={e => this.setState({useShopifyPayment: e.target.value})}
					/>
					<label>No</label>
				</div>
			</div>
			{useShopifyPayment === 'no' && <div className="g">
				<span>What are your external payment gateway fees?</span>
				<div style={{display: 'flex'}}>
					<div className="ig">
						<input
							type="number"
							defaultValue={externalPaymentGatewayFeesPercentage}
							onChange={e => this.setState({externalPaymentGatewayFeesPercentage: e.target.value})}
						/>
						<span>%</span>
					</div>
					<span className="sep">+</span>
					<div className="ig">
						<span>$</span>
						<input
							type="number"
							defaultValue={externalPaymentGatewayFeesFixed}
							onChange={e => this.setState({externalPaymentGatewayFeesFixed: e.target.value})}
						/>
					</div>
				</div>
			</div>}
			<table>
				<thead>
					<tr>
						<td></td>
						{Object.keys(shopifyPlans).map((plan, idx) => (
							<th key={`th-${idx}`} align="center">{plan}</th>
						))}
					</tr>
				</thead>
				<tbody>
					<tr>
						<th>Monthly price</th>
						{Object.values(shopifyPlans).map((plan, idx) => (
							<td key={`mp-${idx}`} align="center">${plan.monthlyPrice}</td>
						))}
					</tr>
					<tr>
						<th>Credit card rate</th>
						{Object.values(shopifyPlans).map((plan, idx) => <td key={`ccr-${idx}`} align="center">{
							useShopifyPayment === 'yes'
								? <>
									${this.calculateFee(
										averageOrderValue,
										numberOfOrderPerMonth,
										plan.creditCardRatePercentage,
										plan.creditCardRateFixed
									)}
									<br/>
									<span className="hint">
										{`${plan.creditCardRatePercentage}% + $${plan.creditCardRateFixed}`}
									</span>
								</>
								: <>
									${this.calculateFee(
										averageOrderValue,
										numberOfOrderPerMonth,
										externalPaymentGatewayFeesPercentage,
										externalPaymentGatewayFeesFixed
									)}
									<br/>
									<span className="hint">
										{`${externalPaymentGatewayFeesPercentage}% + $${externalPaymentGatewayFeesFixed}`}
									</span>
								</>
						}</td>)}
					</tr>
					<tr>
						<th>3rd-party transaction fee</th>
						{Object.values(shopifyPlans).map((plan, idx) => <td key={`tt-${idx}`} align="center">{
							useShopifyPayment === 'yes'
								? 'None'
								: <>
									${this.calculateFee(
										averageOrderValue,
										numberOfOrderPerMonth,
										plan.transactionFee
									)}
									<br/>
									<span className="hint">
										{`${plan.transactionFee}%`}
									</span>
								</>
						}</td>)}
					</tr>
				</tbody>
				<tfoot>
					<tr>
						<th>Total cost</th>
						{Object.values(shopifyPlans).map((plan, idx) => <td key={`tc-${idx}`} align="center">${
							useShopifyPayment === 'yes'
								? plan.monthlyPrice + this.calculateFee(
									averageOrderValue,
									numberOfOrderPerMonth,
									plan.creditCardRatePercentage,
									plan.creditCardRateFixed
								)
								: plan.monthlyPrice + this.calculateFee(
									averageOrderValue,
									numberOfOrderPerMonth,
									externalPaymentGatewayFeesPercentage,
									externalPaymentGatewayFeesFixed
								) + this.calculateFee(
									averageOrderValue,
									numberOfOrderPerMonth,
									plan.transactionFee
								)
						}</td>)}
					</tr>
				</tfoot>
			</table>
		</div>
	}
}
